import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { colors, fonts, radii, shadows } from './tokens';
import { MicIcon, DotsVerticalIcon } from './icons';
import { useVideo } from '../context/videoContext';
import { useAttachTrack, useParticipantTracks } from '../helpers/video/useParticipantTracks';
import { describeMediaError } from '../helpers/video/media';
import { LocalTileStatus, RemoteTileStatus, ReconnectingBanner, WarningBanner } from '../2022-component-library/src/components/shared/VideoTileStatus';
import { useSpeaking } from './useSpeaking';

// Initials for the avatar placeholder ("Della Craig" -> "DC").
export const initialsOf = (name = '') => {
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return (first + last).toUpperCase();
};

const Root = styled.div`
  position: relative;
  isolation: isolate;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  border-radius: ${({ radius }) => radius}px;
  background: ${colors.videoBg};
  container-type: size;
  font-family: ${fonts.family};

  video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 6;
    border-radius: inherit;
    pointer-events: none;
    box-shadow: ${({ active }) => (active ? `inset 0 0 0 3px ${colors.tableHeader}` : 'none')};
    transition: box-shadow 150ms ease;
  }
`;

const AvatarLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.videoBg};
`;

const Avatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: min(56cqh, 60cqw);
  aspect-ratio: 1;
  border-radius: 50%;
  background: ${colors.avatar};
  color: #fff;
  font-weight: 700;
  font-size: min(20cqh, 22cqw);
  letter-spacing: 0.02em;
  margin-bottom: 6cqh;
`;

const Plate = styled.div`
  position: absolute;
  z-index: 5;
  bottom: 8px;
  max-width: calc(100% - 56px);
  padding: 4px 8px;
  border-radius: 8.8px;
  color: #fff;
  font-size: 13px;
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ centered }) => (centered ? 'left:50%;transform:translateX(-50%);background:transparent;' : `left:8px;background:${colors.namePlate};`)}
  b {
    font-weight: 700;
  }
  @container (max-width: 200px) {
    font-size: 11px;
    padding: 2px 6px;
    bottom: 6px;
  }
`;

const MutedBadge = styled.div`
  position: absolute;
  z-index: 5;
  top: 8px;
  right: 8px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.micOffBadge};
  color: #fff;
  @container (max-width: 200px) {
    width: 28px;
    height: 28px;
    svg {
      width: 15px;
      height: 15px;
    }
  }
`;

const TopBanner = styled.div`
  position: absolute;
  z-index: 7;
  top: 0;
  left: 0;
  right: 0;
  height: 44px;
`;

const MenuButton = styled.button`
  position: absolute;
  z-index: 7;
  bottom: 8px;
  right: 8px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  cursor: pointer;
`;

const TileMenu = styled.ul`
  position: absolute;
  z-index: 8;
  right: 8px;
  bottom: 44px;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border-radius: 6px;
  box-shadow: ${shadows.menu};
  min-width: 160px;
  button {
    width: 100%;
    padding: 10px 14px;
    border: 0;
    background: transparent;
    text-align: left;
    font: 500 13px ${fonts.family};
    color: ${colors.slate[700]};
    cursor: pointer;
    &:hover {
      background: ${colors.slate[50]};
    }
  }
`;

/**
 * VideoTileFrame - presentational tile (use LocalVideoTile / RemoteVideoTile in the app).
 * @param roleLabel  "Clinician" | "Patient" (bold part of the name chip)
 * @param name       participant name
 * @param showAvatar render the teal initials avatar instead of the video (camera off / not joined)
 * @param muted      show the red mic-off badge
 * @param active     teal active-speaker border
 * @param overlay    status overlay node (connection / permission errors)
 * @param banner     warning banner node (shown at the top)
 * @param caption    small text under the avatar ("Waiting to join…")
 * @param menuItems  [{label, onSelect}] -> the "⋮" menu (bottom-right)
 */
export const VideoTileFrame = ({ roleLabel, name, showAvatar, muted, active, overlay, banner, caption, menuItems, radius = radii.tile, children, ...rest }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const label = (
    <>
      {roleLabel && <b>{roleLabel}:</b>} {name}
    </>
  );
  return (
    <Root radius={radius} active={active} data-active={active ? 'true' : undefined} {...rest}>
      {children}
      {showAvatar && !overlay && (
        <AvatarLayer>
          <Avatar aria-hidden='true'>{initialsOf(name)}</Avatar>
        </AvatarLayer>
      )}
      {overlay}
      {banner && <TopBanner>{banner}</TopBanner>}
      {muted && !overlay && (
        <MutedBadge aria-label={`${name || roleLabel} is muted`} role='img'>
          <MicIcon off size={18} />
        </MutedBadge>
      )}
      {(name || roleLabel) && (!overlay || showAvatar) && (
        <Plate centered={showAvatar}>
          {label}
          {caption && showAvatar && <span style={{ opacity: 0.75 }}> · {caption}</span>}
        </Plate>
      )}
      {menuItems?.length > 0 && (
        <>
          <MenuButton type='button' aria-label='Video options' aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
            <DotsVerticalIcon size={16} strokeWidth={3} />
          </MenuButton>
          {menuOpen && (
            <TileMenu role='menu'>
              {menuItems.map((m) => (
                <li key={m.label} role='none'>
                  <button
                    type='button'
                    role='menuitem'
                    onClick={() => {
                      setMenuOpen(false);
                      m.onSelect();
                    }}
                  >
                    {m.label}
                  </button>
                </li>
              ))}
            </TileMenu>
          )}
        </>
      )}
    </Root>
  );
};

const LOCAL_BLOCKING = ['requesting-permission', 'permission-denied', 'no-device', 'device-in-use', 'failed'];

// Mirrors LocalTileStatus / RemoteTileStatus: true when they render an overlay (not null).
export const localStatusVisible = (mediaStatus, hasVideo, isCameraEnabled) => {
  if (mediaStatus === 'idle') return !hasVideo;
  if (LOCAL_BLOCKING.includes(mediaStatus)) return true;
  return !hasVideo || !isCameraEnabled;
};

export const remoteStatusVisible = (connectionStatus, participant, hasVideo, isVideoEnabled) => {
  if (['failed', 'disconnected', 'idle', 'connecting'].includes(connectionStatus)) return true;
  return !participant || !hasVideo || !isVideoEnabled;
};

/**
 * LocalVideoTile - this user's camera. Keeps the <video id={localParticipant.sid}> contract that
 * corners-fixation and useFaceApi rely on, and the LocalTileStatus error states (permission
 * blocked, no device, in use, failed) restyled dark. Camera off -> initials avatar.
 */
export const LocalVideoTile = ({ roleLabel, name, parentVideoRef, showMenu = false, ...rest }) => {
  const video = useVideo() || {};
  const { room, localTracks, mediaStatus, mediaError, permissionState, audioError, publishError, isCameraEnabled, isMicEnabled, retry, reconnect } = video;
  const videoTrack = localTracks?.video || null;
  const audioTrack = localTracks?.audio || null;
  const videoRef = useRef(null);
  useAttachTrack(videoTrack, videoRef);
  useEffect(() => {
    if (parentVideoRef && videoRef.current && parentVideoRef.current !== videoRef.current) parentVideoRef.current = videoRef.current;
  });
  const speaking = useSpeaking(audioTrack, { enabled: Boolean(isMicEnabled) });

  const hasVideo = Boolean(videoTrack);
  const cameraOff = hasVideo && isCameraEnabled === false && !LOCAL_BLOCKING.includes(mediaStatus);
  const statusVisible = !cameraOff && localStatusVisible(mediaStatus, hasVideo, isCameraEnabled);
  const status = !statusVisible ? null : (
    <LocalTileStatus
      tone='dark'
      mediaStatus={mediaStatus}
      mediaError={mediaError}
      permissionState={permissionState}
      hasVideo={Boolean(videoTrack)}
      isCameraEnabled={isCameraEnabled}
      onRetry={retry}
    />
  );

  let banner = null;
  if (publishError && room) {
    banner = (
      <WarningBanner actionLabel='Retry' onAction={retry}>
        Your video is not reaching the other participant: {publishError.message}
      </WarningBanner>
    );
  } else if (audioError && videoTrack) {
    banner = <WarningBanner>Microphone unavailable: {describeMediaError(audioError, 'microphone')}</WarningBanner>;
  }

  return (
    <VideoTileFrame
      roleLabel={roleLabel}
      name={name}
      showAvatar={cameraOff}
      muted={!audioTrack || isMicEnabled === false}
      active={speaking}
      overlay={status}
      banner={banner}
      menuItems={showMenu && reconnect ? [{ label: 'Refresh video', onSelect: () => reconnect() }] : undefined}
      data-testid='local-video-tile'
      {...rest}
    >
      <video ref={videoRef} id={room?.localParticipant?.sid} autoPlay playsInline muted style={{ visibility: videoTrack && !cameraOff ? 'visible' : 'hidden' }} />
    </VideoTileFrame>
  );
};

/**
 * RemoteVideoTile - the other participant. Connection states (connecting / failed / disconnected
 * / reconnecting banner) come from RemoteTileStatus; "camera off", "no camera" and "not joined
 * yet" show the initials avatar (with a caption while waiting).
 */
export const RemoteVideoTile = ({ roleLabel, name, parentVideoRef, ...rest }) => {
  const { participants, connectionStatus, connectionError, retry } = useVideo() || {};
  const participant = participants || null;
  const { videoTrack, audioTrack, isVideoEnabled } = useParticipantTracks(participant);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  useAttachTrack(videoTrack, videoRef);
  useAttachTrack(audioTrack, audioRef);
  useEffect(() => {
    if (parentVideoRef && videoRef.current && parentVideoRef.current !== videoRef.current) parentVideoRef.current = videoRef.current;
  });
  const speaking = useSpeaking(audioTrack);

  const connected = connectionStatus === 'connected' || connectionStatus === 'reconnecting';
  const waiting = connected && !participant;
  const cameraOff = connected && participant && (!videoTrack ? !participant.videoTracks?.size : !isVideoEnabled);
  const statusVisible = !waiting && !cameraOff && remoteStatusVisible(connectionStatus, participant, Boolean(videoTrack), isVideoEnabled);
  const status = !statusVisible ? null : (
      <RemoteTileStatus
        tone='dark'
        connectionStatus={connectionStatus}
        connectionError={connectionError}
        participant={participant}
        hasVideo={Boolean(videoTrack)}
        isVideoEnabled={isVideoEnabled}
        onRetry={retry}
      />
    );

  return (
    <VideoTileFrame
      roleLabel={roleLabel}
      name={name}
      showAvatar={waiting || cameraOff}
      caption={waiting ? 'waiting to join…' : undefined}
      muted={Boolean(participant) && (!audioTrack || audioTrack.isEnabled === false)}
      active={speaking}
      overlay={status}
      banner={connectionStatus === 'reconnecting' ? <ReconnectingBanner /> : null}
      data-testid='remote-video-tile'
      data-state={waiting ? 'waiting' : cameraOff ? 'camera-off' : connectionStatus}
      {...rest}
    >
      <audio ref={audioRef} autoPlay />
      <video ref={videoRef} autoPlay playsInline muted id={participant?.sid} style={{ visibility: videoTrack && !cameraOff ? 'visible' : 'hidden' }} />
    </VideoTileFrame>
  );
};

export default VideoTileFrame;
